import ListingCardBadges from './ListingCardBadges.jsx';
import ListingCardBody from './ListingCardBody.jsx';
import ListingCardDescription from './ListingCardDescription.jsx';
import ListingCardHeader from './ListingCardHeader.jsx';
import ListingCardMedia from './ListingCardMedia.jsx';
import ListingCardMeta from './ListingCardMeta.jsx';
import ListingCardOfferStrip from './ListingCardOfferStrip.jsx';
import ListingCardRating from './ListingCardRating.jsx';
import ListingCardShell from './ListingCardShell.jsx';
import {
  cn,
  getListingHref,
  getTierClasses,
  normalizeListing,
  resolveListingCardProps,
} from './listingCardUtils.js';

function FavoriteButton({ active = false, disabled = false, onClick }) {
  return (
    <button
      type="button"
      aria-label={active ? 'Remove favorite' : 'Add favorite'}
      aria-pressed={active}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-sm shadow-sm transition',
        active ? 'border-rose-200 text-rose-600' : 'border-slate-200 text-slate-500 hover:text-slate-900',
        disabled ? 'cursor-not-allowed opacity-50' : '',
      )}
    >
      ♥
    </button>
  );
}

function CardAvatar({ listing, tier = 'basic' }) {
  const tierClasses = getTierClasses(tier);

  return (
    <div className={cn('flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-white text-sm font-bold shadow-md', tierClasses.accent)}>
      {listing.logoUrl ? (
        <img src={listing.logoUrl} alt="" className="h-full w-full object-contain p-1.5" />
      ) : (
        <span>{listing.initials}</span>
      )}
    </div>
  );
}

function CardTitle({ listing, variant = 'content' }) {
  const href = getListingHref(listing);
  const className = cn(
    'truncate font-bold text-slate-950',
    variant === 'content' ? 'text-lg leading-6' : 'text-[17px] leading-6',
    href ? 'hover:text-[color:var(--listing-card-primary-hover,#1D4ED8)]' : '',
  );

  if (!href) return <span className={className}>{listing.name}</span>;

  return (
    <a href={href} className={className}>
      {listing.name}
    </a>
  );
}

export default function ListingCard({
  listing,
  variant,
  context = 'public',
  density = 'comfortable',
  tier,
  media,
  show,
  onClick,
  onFavorite,
  selected = false,
  disabled = false,
  claimed = false,
  className,
}) {
  const normalizedListing = normalizeListing(listing);
  const resolved = resolveListingCardProps({
    variant,
    context,
    density,
    tier: tier || normalizedListing.tier,
    media,
    show,
  });

  if (resolved.variant === 'text') {
    return (
      <ListingCardShell
        variant={resolved.variant}
        tier={resolved.tier}
        density={resolved.density}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        className={className}
      >
        <ListingCardHeader listing={normalizedListing} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
      </ListingCardShell>
    );
  }

  if (resolved.variant === 'chip') {
    return (
      <ListingCardShell
        variant={resolved.variant}
        tier={resolved.tier}
        density={resolved.density}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        className={className}
      >
        <ListingCardHeader listing={normalizedListing} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
      </ListingCardShell>
    );
  }

  if (resolved.variant === 'row') {
    return (
      <ListingCardShell
        variant={resolved.variant}
        tier={resolved.tier}
        density={resolved.density}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        className={className}
      >
        <ListingCardMedia listing={normalizedListing} media={resolved.media} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
        <ListingCardBody variant={resolved.variant}>
          <ListingCardHeader listing={normalizedListing} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
          <ListingCardMeta listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
        </ListingCardBody>
      </ListingCardShell>
    );
  }

  if (resolved.variant === 'content') {
    return (
      <ListingCardShell
        variant={resolved.variant}
        tier={resolved.tier}
        density={resolved.density}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        className={className}
      >
        <div className="relative">
          <ListingCardMedia listing={normalizedListing} media={resolved.media} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
          {resolved.show.favorite ? (
            <div className="absolute right-3 top-3">
              <FavoriteButton disabled={disabled} onClick={() => onFavorite?.(normalizedListing)} />
            </div>
          ) : null}
          {resolved.show.logo ? (
            <div className="absolute bottom-0 left-4 translate-y-1/2">
              <CardAvatar listing={normalizedListing} tier={resolved.tier} />
            </div>
          ) : null}
        </div>
        <ListingCardBody variant={resolved.variant}>
          <div className="min-w-0">
            <CardTitle listing={normalizedListing} variant={resolved.variant} />
          </div>
          <ListingCardRating listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
          {resolved.show.category && normalizedListing.category ? (
            <p className="truncate text-xs text-slate-500">{normalizedListing.category}</p>
          ) : null}
          <ListingCardDescription listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
          <ListingCardMeta listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
          <ListingCardBadges listing={normalizedListing} show={resolved.show} tier={resolved.tier} variant={resolved.variant} claimed={claimed} />
          <ListingCardOfferStrip listing={normalizedListing} show={resolved.show} tier={resolved.tier} variant={resolved.variant} />
        </ListingCardBody>
      </ListingCardShell>
    );
  }

  if (resolved.variant === 'directory') {
    return (
      <ListingCardShell
        variant={resolved.variant}
        tier={resolved.tier}
        density={resolved.density}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
        className={className}
      >
        <div className="relative">
          <ListingCardMedia listing={normalizedListing} media={resolved.media} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
          {resolved.show.favorite ? (
            <div className="absolute right-3 top-3">
              <FavoriteButton disabled={disabled} onClick={() => onFavorite?.(normalizedListing)} />
            </div>
          ) : null}
          {resolved.show.logo ? (
            <div className="absolute bottom-0 left-4 translate-y-1/2">
              <CardAvatar listing={normalizedListing} tier={resolved.tier} />
            </div>
          ) : null}
        </div>
        <ListingCardBody variant={resolved.variant}>
          <div className="min-w-0">
            <CardTitle listing={normalizedListing} variant={resolved.variant} />
            {resolved.show.category && normalizedListing.category ? (
              <p className="mt-0.5 truncate text-xs text-slate-500">{normalizedListing.category}</p>
            ) : null}
          </div>
          <ListingCardRating listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
          <ListingCardBadges listing={normalizedListing} show={resolved.show} tier={resolved.tier} variant={resolved.variant} claimed={claimed} />
          <ListingCardDescription listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
          <ListingCardMeta listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
          <ListingCardOfferStrip listing={normalizedListing} show={resolved.show} tier={resolved.tier} variant={resolved.variant} />
        </ListingCardBody>
      </ListingCardShell>
    );
  }

  return (
    <ListingCardShell
      variant={resolved.variant}
      tier={resolved.tier}
      density={resolved.density}
      selected={selected}
      disabled={disabled}
      onClick={onClick}
      className={className}
    >
      <ListingCardMedia listing={normalizedListing} media={resolved.media} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
      <ListingCardBody variant={resolved.variant}>
        <div className="flex items-start justify-between gap-3">
          <ListingCardHeader listing={normalizedListing} variant={resolved.variant} tier={resolved.tier} show={resolved.show} />
          {resolved.show.favorite ? (
            <FavoriteButton disabled={disabled} onClick={() => onFavorite?.(normalizedListing)} />
          ) : null}
        </div>
        <ListingCardBadges listing={normalizedListing} show={resolved.show} tier={resolved.tier} variant={resolved.variant} claimed={claimed} />
        <ListingCardRating listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
        <ListingCardDescription listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
        <ListingCardMeta listing={normalizedListing} show={resolved.show} variant={resolved.variant} />
        <ListingCardOfferStrip listing={normalizedListing} show={resolved.show} tier={resolved.tier} variant={resolved.variant} />
      </ListingCardBody>
    </ListingCardShell>
  );
}

export { resolveListingCardProps, normalizeListing };
